// src/pages/InfectionDashboard.jsx
//
// Global Threat Intelligence Dashboard — denser, SOC-terminal feel.
// Sections are numbered and labeled like analyst views; KPIs carry inline
// sparklines so direction is readable at a glance; hairline dividers and
// rounded-md cards keep it closer to Datadog/Linear than a marketing page.

import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { redirectToLogin } from "../utils/auth";
import {
  ResponsiveContainer,
  Area,
  Tooltip as RTooltip,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
} from "recharts";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Globe,
  RefreshCw,
  Layers,
  Radio,
  ArrowUpRight,
  Newspaper,
} from "lucide-react";

const API_URL =
  "https://whiteintel.io/api/regular/app/v2/dashboard/global_stats_v2.php";

// World atlas topojson — numeric ISO 3166-1 country IDs.
const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO-2 → numeric (matches what the topojson uses for `geo.id`). Lifted from
// the previous dashboard. Only includes the 200ish countries the response is
// likely to carry; missing codes simply render dimmed.
const ISO2_TO_NUMERIC = {
  AD: "020", AE: "784", AF: "004", AG: "028", AI: "660", AL: "008", AM: "051",
  AO: "024", AR: "032", AS: "016", AT: "040", AU: "036", AW: "533", AX: "248",
  AZ: "031", BA: "070", BB: "052", BD: "050", BE: "056", BF: "854", BG: "100",
  BH: "048", BI: "108", BJ: "204", BL: "652", BM: "060", BN: "096", BO: "068",
  BQ: "535", BR: "076", BS: "044", BT: "064", BV: "074", BW: "072", BY: "112",
  BZ: "084", CA: "124", CC: "166", CD: "180", CF: "140", CG: "178", CH: "756",
  CI: "384", CK: "184", CL: "152", CM: "120", CN: "156", CO: "170", CR: "188",
  CU: "192", CV: "132", CW: "531", CX: "162", CY: "196", CZ: "203", DE: "276",
  DJ: "262", DK: "208", DM: "212", DO: "214", DZ: "012", EC: "218", EE: "233",
  EG: "818", EH: "732", ER: "232", ES: "724", ET: "231", FI: "246", FJ: "242",
  FK: "238", FM: "583", FO: "234", FR: "250", GA: "266", GB: "826", GD: "308",
  GE: "268", GF: "254", GG: "831", GH: "288", GI: "292", GL: "304", GM: "270",
  GN: "324", GP: "312", GQ: "226", GR: "300", GS: "239", GT: "320", GU: "316",
  GW: "624", GY: "328", HK: "344", HM: "334", HN: "340", HR: "191", HT: "332",
  HU: "348", ID: "360", IE: "372", IL: "376", IM: "833", IN: "356", IO: "086",
  IQ: "368", IR: "364", IS: "352", IT: "380", JE: "832", JM: "388", JO: "400",
  JP: "392", KE: "404", KG: "417", KH: "116", KI: "296", KM: "174", KN: "659",
  KP: "408", KR: "410", KW: "414", KY: "136", KZ: "398", LA: "418", LB: "422",
  LC: "662", LI: "438", LK: "144", LR: "430", LS: "426", LT: "440", LU: "442",
  LV: "428", LY: "434", MA: "504", MC: "492", MD: "498", ME: "499", MF: "663",
  MG: "450", MH: "584", MK: "807", ML: "466", MM: "104", MN: "496", MO: "446",
  MP: "580", MQ: "474", MR: "478", MS: "500", MT: "470", MU: "480", MV: "462",
  MW: "454", MX: "484", MY: "458", MZ: "508", NA: "516", NC: "540", NE: "562",
  NF: "574", NG: "566", NI: "558", NL: "528", NO: "578", NP: "524", NR: "520",
  NU: "570", NZ: "554", OM: "512", PA: "591", PE: "604", PF: "258", PG: "598",
  PH: "608", PK: "586", PL: "616", PM: "666", PN: "612", PR: "630", PS: "275",
  PT: "620", PW: "585", PY: "600", QA: "634", RE: "638", RO: "642", RS: "688",
  RU: "643", RW: "646", SA: "682", SB: "090", SC: "690", SD: "729", SE: "752",
  SG: "702", SH: "654", SI: "705", SJ: "744", SK: "703", SL: "694", SM: "674",
  SN: "686", SO: "706", SR: "740", SS: "728", ST: "678", SV: "222", SX: "534",
  SY: "760", SZ: "748", TC: "796", TD: "148", TF: "260", TG: "768", TH: "764",
  TJ: "762", TK: "772", TL: "626", TM: "795", TN: "788", TO: "776", TR: "792",
  TT: "780", TV: "798", TW: "158", TZ: "834", UA: "804", UG: "800", UM: "581",
  US: "840", UY: "858", UZ: "860", VA: "336", VC: "670", VE: "862", VG: "092",
  VI: "850", VN: "704", VU: "548", WF: "876", WS: "882", XK: "412", YE: "887",
  YT: "175", ZA: "710", ZM: "894", ZW: "716",
};

const cardClass =
  "rounded-md border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md";

/* ================== helpers ================== */

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

const fmtCompact = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000_000) return (v / 1_000_000_000).toFixed(2) + "B";
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(2) + "M";
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return v.toLocaleString();
};

const fmtFull = (n) =>
  n == null || isNaN(n) ? "—" : Number(n).toLocaleString();

const fmtTs = (ts) => (ts ? String(ts).replace("T", " ").slice(0, 16) : "—");

const fmtRelative = (ts) => {
  if (!ts) return "";
  try {
    const d = new Date(String(ts).replace(" ", "T") + "Z");
    const diffMs = Date.now() - d.getTime();
    const m = Math.round(diffMs / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const dd = Math.round(h / 24);
    return `${dd}d ago`;
  } catch {
    return "";
  }
};

/* ================== sub-components ================== */

function Sparkline({ data, color = "#38bdf8" }) {
  if (!data || data.length === 0) {
    return <div className="h-8 w-full bg-white/[0.02] rounded-sm" />;
  }
  return (
    <div className="h-8 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
          <defs>
            <linearGradient id={`sparkFill-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#sparkFill-${color.replace("#", "")})`}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiCard({
  eyebrow,
  label,
  value,
  delta,
  sparkData,
  sparkColor = "#38bdf8",
  help,
  onClick,
  navHint,
}) {
  const deltaUp = typeof delta === "number" && delta > 0;
  const deltaDown = typeof delta === "number" && delta < 0;
  const deltaTone = deltaUp
    ? "text-emerald-300"
    : deltaDown
    ? "text-rose-300"
    : "text-neutral-500";
  const clickable = typeof onClick === "function";
  const interactive = clickable
    ? "group cursor-pointer hover:border-sky-500/40 hover:bg-[#0a1224]/90 transition-colors"
    : "";
  const inner = (
    <div className={`${cardClass} ${interactive} p-4 flex flex-col gap-3 relative`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
          {eyebrow}
        </div>
        <div className="flex items-center gap-2">
          {typeof delta === "number" && (
            <div className={`inline-flex items-center gap-0.5 text-[11px] font-mono font-semibold ${deltaTone}`}>
              {deltaUp ? <TrendingUp className="h-3 w-3" /> : deltaDown ? <TrendingDown className="h-3 w-3" /> : null}
              {delta > 0 ? "+" : ""}
              {delta.toFixed(1)}%
            </div>
          )}
          {clickable && (
            <ArrowUpRight className="h-3.5 w-3.5 text-neutral-600 group-hover:text-sky-300 transition-colors" />
          )}
        </div>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="text-[26px] font-semibold text-white tabular-nums leading-none">{value}</div>
          <div className="mt-1.5 text-[11px] text-neutral-400 font-medium">{label}</div>
        </div>
      </div>
      {sparkData && sparkData.length > 0 ? (
        <Sparkline data={sparkData} color={sparkColor} />
      ) : (
        help && <div className="text-[10px] text-neutral-500 font-mono">{help}</div>
      )}
      {clickable && navHint && (
        <div className="text-[10px] text-neutral-600 font-mono uppercase tracking-wider group-hover:text-sky-400 transition-colors">
          {navHint} →
        </div>
      )}
    </div>
  );
  if (!clickable) return inner;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded-md"
    >
      {inner}
    </button>
  );
}

function SectionHeader({ title, description, right }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3 mt-6 first:mt-0">
      <div className="min-w-0">
        <div className="text-[13px] text-neutral-200 font-semibold">
          {title}
        </div>
        {description && (
          <div className="mt-0.5 text-[11px] text-neutral-500">{description}</div>
        )}
      </div>
      {right}
    </div>
  );
}

function CardHeader({ icon: Icon, title, right, accent }) {
  // Optional accent — soft tinted gradient + faint text glow. Used to give
  // certain cards a moody atmosphere (e.g. dark-web chatter = rose) without
  // being a solid colored bar.
  const accentMap = {
    rose: {
      bg: "from-rose-500/10 via-rose-500/[0.03] to-transparent",
      title: "",
      icon: "text-rose-300/90",
    },
  };
  const a = accent ? accentMap[accent] : null;
  return (
    <div className="relative shrink-0 flex items-center justify-between gap-3 px-4 h-12 border-b border-sky-900/30 overflow-hidden">
      {a && (
        <div className={`pointer-events-none absolute inset-0 bg-gradient-to-r ${a.bg}`} />
      )}
      <div className="relative flex items-center gap-2 min-w-0">
        {Icon && <Icon className={`h-3.5 w-3.5 shrink-0 ${a?.icon || "text-sky-300/80"}`} />}
        <h3 className={`text-[12px] font-semibold text-neutral-200 tracking-tight truncate ${a?.title || ""}`}>
          {title}
        </h3>
      </div>
      <div className="relative shrink-0 whitespace-nowrap">{right}</div>
    </div>
  );
}

function Skeleton({ className = "" }) {
  return (
    <div className={`bg-white/[0.04] rounded-sm animate-pulse ${className}`} />
  );
}

function KpiSkeleton() {
  return (
    <div className={`${cardClass} p-4 flex flex-col gap-3`}>
      <div className="flex items-start justify-between">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-10" />
      </div>
      <Skeleton className="h-7 w-20" />
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

function CardSkeleton({ rows = 6, height }) {
  return (
    <div className={cardClass}>
      <div className="px-4 h-12 border-b border-sky-900/30 flex items-center justify-between">
        <Skeleton className="h-3.5 w-40" />
        <Skeleton className="h-3 w-12" />
      </div>
      <div className="p-4 space-y-3" style={height ? { height } : undefined}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-2 w-32 shrink-0" />
            <Skeleton className="h-1.5 flex-1" style={{ width: `${50 + ((i * 13) % 45)}%` }} />
            <Skeleton className="h-2 w-10 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartSkeleton({ height = 280 }) {
  return (
    <div className={cardClass}>
      <div className="p-4" style={{ height }}>
        <Skeleton className="h-full w-full" />
      </div>
    </div>
  );
}

/* ================== main ================== */

export default function InfectionDashboard() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        cache: "no-store",
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // Endpoint no longer carries a top-level `status` field — accept any
      // 2xx with a meta block. Surface explicit errors only when the backend
      // ships them.
      if (json?.error) throw new Error(json.error);
      if (!json?.meta) throw new Error("Unexpected response shape");
      setData(json);
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll, refreshKey]);

  /* ---- derived ---- */
  const isf = data?.infostealer || {};
  const tf = data?.threat_feed || {};
  const meta = data?.meta || {};
  const sc = data?.scorecard || {};

  // Normalized sparkline series for KPIs (last ~14 days). Both series live on
  // the infostealer block now (the response no longer carries a `combined`
  // bucket); the threat-feed sparkline reuses the leaks series as a proxy
  // for activity since the per-day post timeline isn't shipped anymore.
  const sparkInfections = useMemo(() => {
    const series = (isf?.velocity || []).slice(-14);
    return series.map((p, i) => ({ i, v: Number(p.infections || 0) }));
  }, [isf]);

  const sparkLeaks = useMemo(() => {
    const series = (isf?.velocity || []).slice(-14);
    return series.map((p, i) => ({ i, v: Number(p.leaks || 0) }));
  }, [isf]);

  const topCountries = useMemo(() => {
    const list = (isf?.map || [])
      .filter((c) => c.id && c.id.length === 2)
      .sort((a, b) => (b.value || 0) - (a.value || 0))
      .slice(0, 10);
    return list.map((c) => ({
      name: c.id,
      total: Number(c.value || 0),
    }));
  }, [isf]);

  // Map keyed by topojson numeric ID for fast lookup during render. Values are
  // log-compressed since the data spans 6 orders of magnitude — a linear scale
  // would make every country except India / Brazil / US look the same shade.
  const { valueByNumeric, maxLogValue } = useMemo(() => {
    const out = {};
    let maxLog = 0;
    for (const c of isf?.map || []) {
      if (!c.id || c.id.length !== 2) continue;
      const num = ISO2_TO_NUMERIC[c.id];
      if (!num) continue;
      const v = Number(c.value || 0);
      out[num] = { iso2: c.id, value: v };
      const log = Math.log10(v + 1);
      if (log > maxLog) maxLog = log;
    }
    return { valueByNumeric: out, maxLogValue: maxLog };
  }, [isf]);

  // Single-hue sequential choropleth (Recorded Future style): one blue ramp
  // from dim (low activity) to bright (hotspots). No multi-hue rainbow, no
  // glow — the saturation carries the signal. No-data countries sit on slate.
  const colorScale = useMemo(() => {
    const m = maxLogValue || 1;
    return scaleLinear()
      .domain([0, m * 0.25, m * 0.5, m * 0.75, m])
      .range(["#10314f", "#15507f", "#1d6fb0", "#2b8fd4", "#5cb8f0"])
      .clamp(true);
  }, [maxLogValue]);

  const riskRadar = useMemo(() => {
    return (isf?.risk_radar || []).map((r) => ({
      type: r.risk_type,
      score: Number(r.score || 0),
    }));
  }, [isf]);

  const surfaceCategories = useMemo(() => {
    return (isf?.categories || [])
      .slice()
      .sort((a, b) => (b.count_last_30d || 0) - (a.count_last_30d || 0))
      .slice(0, 10)
      .map((c) => ({
        name: c.name,
        value: Number(c.count_last_30d || 0),
      }));
  }, [isf]);

  /* ================== render ================== */

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header bar — terminal-style */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              Global Trends
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 px-3 py-1.5 text-xs text-neutral-200 hover:text-white hover:bg-white/[0.04] disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="globalstats"
          onChange={() => {}}
        />

        <main className="flex-1 min-w-0 px-4 md:px-6 pb-12 pt-5">
          {error && (
            <div className="mb-4 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {loading && !data ? (
            <>
              {/* SKELETON — shown only on initial load. Refreshes keep the
                  current data visible and surface the spinner on the Refresh
                  button instead of blowing away the layout. */}
              <SectionHeader index={1} title="Overview" description="Headline volumes and 24-hour movement." />
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <KpiSkeleton key={i} />
                ))}
              </div>
              <SectionHeader
                index={2}
                title="Real-time activity"
                description="Dark-web chatter, open-source coverage, and which credential surfaces are being hit hardest."
              />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <CardSkeleton rows={8} />
                <CardSkeleton rows={8} />
                <CardSkeleton rows={10} />
              </div>
              <SectionHeader
                index={3}
                title="Geography & Risk"
                description="Highest-volume countries and current risk surface."
              />
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                <div className="lg:col-span-3">
                  <CardSkeleton rows={10} />
                </div>
                <div className="lg:col-span-2">
                  <ChartSkeleton height={300} />
                </div>
              </div>
            </>
          ) : (
          <>
          {/* ============ 01 OVERVIEW (KPI strip) ============ */}
          <SectionHeader
            index={1}
            title="Overview"
            description="Headline volumes and 24-hour movement."
          />
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <KpiCard
              eyebrow="Credentials · 30d"
              label="Records ingested"
              value={fmtCompact(sc?.credentials_30d)}
              sparkData={sparkInfections}
              sparkColor="#38bdf8"
              help="Leaked credentials ingested in last 30 days."
              onClick={() => navigate("/search")}
              navHint="Search records"
            />
            <KpiCard
              eyebrow="Infected systems · 30d"
              label="Compromised hosts"
              value={fmtCompact(sc?.infected_systems_30d)}
              help="Infostealer infected systems ingested in last 30 days."
            />
            <KpiCard
              eyebrow="Threat feed · 24h"
              label="Adversary posts"
              value={fmtCompact(sc?.total_events_24h)}
              delta={sc?.total_events_change_pct}
              sparkData={sparkLeaks}
              sparkColor="#a78bfa"
              help="Adversary posts ingested in last 24 hours."
              onClick={() => navigate("/threat-feeds")}
              navHint="Open feed"
            />
            <KpiCard
              eyebrow="Adversaries · 30d"
              label="Active actors"
              value={fmtCompact(sc?.active_adversaries_30d)}
              help={`${fmtCompact(sc?.targeted_sectors_30d)} sectors`}
            />
            <KpiCard
              eyebrow="Ransomware · 24h"
              label="New victims"
              value={fmtCompact(sc?.ransomware_victims_24h)}
              help="Ransomware victims posted in last 24 hours."
              onClick={() => navigate("/threat-feeds?tab=chatters&category=ransomware")}
              navHint="View victims"
            />
          </div>

          {/* ============ 02 LIVE ACTIVITY & SURFACES ============ */}
          <SectionHeader
            index={2}
            title="Real-time activity"
            description="Dark-web chatter, open-source coverage, and which credential surfaces are being hit hardest."
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className={`${cardClass} flex flex-col lg:h-[560px]`}>
              <CardHeader
                icon={Radio}
                title="Dark web chatters"
                accent="rose"
                right={
                  <button
                    type="button"
                    onClick={() => navigate("/threat-feeds")}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-300 hover:text-sky-200"
                  >
                    View feed
                    <ArrowUpRight className="h-3 w-3" />
                  </button>
                }
              />
              <ul className="divide-y divide-neutral-800/50 flex-1 min-h-0 overflow-y-auto">
                {(tf?.live_feed || []).map((v) => (
                  <li
                    key={v.id}
                    className="px-4 py-2.5 flex items-start gap-3 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="text-[10px] font-mono text-neutral-600 mt-0.5 w-14 shrink-0 tabular-nums">
                      #{v.id}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-white font-medium truncate" title={v.victim_name}>
                        {v.victim_name || "—"}
                      </div>
                      <div className="text-[10px] text-neutral-500 mt-0.5 truncate font-mono uppercase tracking-wider">
                        {[
                          v.victim_industry,
                          v.victim_country && v.victim_country !== "Unknown"
                            ? v.victim_country
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "Unknown"}
                      </div>
                      {v.actor_name && (
                        <div className="text-[10px] text-violet-300/80 mt-0.5 font-mono truncate">
                          ↳ {v.actor_name}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className="inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-mono border-rose-500/30 bg-rose-500/10 text-rose-200">
                        {v.attack_type || "—"}
                      </span>
                      <div className="mt-1 text-[10px] text-neutral-500 font-mono tabular-nums">
                        {fmtRelative(v.published_at)}
                      </div>
                    </div>
                  </li>
                ))}
                {(!tf?.live_feed || tf.live_feed.length === 0) && (
                  <li className="py-6 text-center text-sm text-neutral-500">No recent victims.</li>
                )}
              </ul>
            </div>

            {/* Public news — open-source coverage. Dark card consistent with
                the rest of the grid (no bright surface / glow). */}
            <div className={`${cardClass} flex flex-col lg:h-[560px]`}>
              <CardHeader
                icon={Newspaper}
                title="Public news"
                right={
                  <button
                    type="button"
                    onClick={() => navigate("/threat-feeds?tab=news")}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-300 hover:text-sky-200"
                  >
                    View all
                    <ArrowUpRight className="h-3 w-3" />
                  </button>
                }
              />
              <ul className="divide-y divide-neutral-800/50 flex-1 min-h-0 overflow-y-auto">
                {(data?.news || []).length > 0 ? (
                  (data?.news || []).slice(0, 12).map((n, i) => (
                    <li key={i}>
                      <a
                        href={n.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2.5 hover:bg-white/[0.02] transition-colors group"
                      >
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-1 min-w-0">
                          <span className="text-sky-300 font-semibold truncate">{n.source || "Unknown"}</span>
                          <span className="text-neutral-600 shrink-0">·</span>
                          <span className="text-neutral-500 font-mono normal-case shrink-0">{fmtRelative(n.published_at)}</span>
                        </div>
                        <h4 className="text-[12px] leading-snug text-neutral-200 font-medium line-clamp-2 group-hover:text-sky-200 transition-colors">
                          {n.title}
                        </h4>
                      </a>
                    </li>
                  ))
                ) : (
                  <li className="py-6 text-center text-sm text-neutral-500">No public coverage right now.</li>
                )}
              </ul>
            </div>

            <div className={`${cardClass} flex flex-col lg:h-[560px]`}>
              <CardHeader
                icon={Globe}
                title="Risk surface"
                right={
                  (() => {
                    const max = riskRadar.reduce(
                      (m, r) => (r.score > m.score ? r : m),
                      { score: 0, type: "—" }
                    );
                    return riskRadar.length > 0 ? (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-rose-300">
                        Top: {max.type} · {max.score}
                      </span>
                    ) : null;
                  })()
                }
              />
              <div className="p-4">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={riskRadar} outerRadius={110}>
                      <PolarGrid stroke="#1e293b" gridType="polygon" />
                      <PolarAngleAxis
                        dataKey="type"
                        tick={{
                          fill: "#cbd5e1",
                          fontSize: 10,
                          fontFamily: "ui-monospace, Menlo, monospace",
                        }}
                      />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        tickCount={5}
                        tick={{
                          fill: "#475569",
                          fontSize: 9,
                          fontFamily: "ui-monospace, Menlo, monospace",
                        }}
                        axisLine={false}
                        stroke="#1e293b"
                      />
                      <Radar
                        name="Risk"
                        dataKey="score"
                        stroke="#0891b2"
                        strokeWidth={2}
                        fill="#0e7490"
                        fillOpacity={0.25}
                        dot={{
                          r: 2.5,
                          fill: "#0891b2",
                          stroke: "#0a1224",
                          strokeWidth: 1.5,
                        }}
                        isAnimationActive={false}
                      />
                      <RTooltip
                        contentStyle={{
                          background: "#0a1224",
                          border: "1px solid rgba(56,189,248,0.3)",
                          borderRadius: 4,
                          fontSize: 11,
                          fontFamily: "ui-monospace, Menlo, monospace",
                        }}
                        formatter={(v) => [`${v} / 100`, "Score"]}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Sorted scoreboard — compact, sits in the bottom half of the
                  same card, with a hairline divider above. */}
              <div className="border-t border-sky-900/30 px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-2">
                  Risk breakdown · sorted
                </div>
                <ul className="space-y-1.5">
                  {riskRadar
                    .slice()
                    .sort((a, b) => b.score - a.score)
                    .map((r, i) => {
                      let tone, labelColor, labelText;
                      if (r.score >= 60) {
                        tone = "bg-rose-600/80";
                        labelColor = "text-rose-300";
                        labelText = "Critical";
                      } else if (r.score >= 40) {
                        tone = "bg-amber-600/80";
                        labelColor = "text-amber-300";
                        labelText = "Elevated";
                      } else if (r.score >= 20) {
                        tone = "bg-sky-600/80";
                        labelColor = "text-sky-300";
                        labelText = "Moderate";
                      } else {
                        tone = "bg-emerald-600/80";
                        labelColor = "text-emerald-300";
                        labelText = "Low";
                      }
                      return (
                        <li key={r.type} className="flex items-center gap-2 text-[11px]">
                          <span className="text-[9px] font-mono text-neutral-600 w-5 tabular-nums shrink-0">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="text-neutral-200 font-medium flex-1 min-w-0 truncate">
                            {r.type}
                          </span>
                          <div className="relative w-16 h-1.5 rounded-full bg-white/[0.04] overflow-hidden shrink-0">
                            <div
                              className={`absolute inset-y-0 left-0 rounded-full ${tone}`}
                              style={{ width: `${Math.max(2, r.score)}%` }}
                            />
                          </div>
                          <span className={`text-[10px] font-mono font-semibold uppercase tracking-wider w-14 text-right shrink-0 ${labelColor}`}>
                            {labelText}
                          </span>
                          <span className="text-[12px] font-mono font-semibold text-white tabular-nums w-7 text-right shrink-0">
                            {r.score}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
          </div>

          {/* ============ 03 GEOGRAPHY & RISK ============ */}
          <SectionHeader
            index={3}
            title="Geography & Risk"
            description="Where infections cluster geographically, and the current risk surface across six dimensions."
          />
          <div className="grid grid-cols-1 lg:grid-cols-5 lg:items-start gap-3">
            <div className={`${cardClass} lg:col-span-3`}>
              <CardHeader
                icon={Globe}
                title="Geographic distribution"
              />
              <div className="relative bg-[#020617]">
                <ComposableMap
                  projectionConfig={{ scale: 145 }}
                  width={800}
                  height={360}
                  style={{ width: "100%", height: "auto", background: "#020617" }}
                >
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const numericId = String(geo.id).padStart(3, "0");
                        const entry = valueByNumeric[numericId];
                        const v = entry?.value || 0;
                        const logV = Math.log10(v + 1);
                        const fill = v > 0 ? colorScale(logV) : "#1e293b";
                        const name = geo.properties?.name || entry?.iso2 || "—";
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            data-tooltip-id="geo-tip"
                            data-tooltip-content={
                              v > 0
                                ? `${name} · ${fmtFull(v)} infections`
                                : `${name} · no data`
                            }
                            style={{
                              default: {
                                fill,
                                stroke: "#0f172a",
                                strokeWidth: 0.35,
                                strokeOpacity: 0.8,
                                outline: "none",
                              },
                              hover: {
                                fill: "#7cc4f5",
                                stroke: "#bfdbfe",
                                strokeWidth: 0.6,
                                strokeOpacity: 1,
                                outline: "none",
                                cursor: "pointer",
                              },
                              pressed: { fill: "#5cb8f0", outline: "none" },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ComposableMap>
                <ReactTooltip
                  id="geo-tip"
                  className="!bg-[#0a1224] !text-neutral-100 !text-[11px] !font-mono !rounded-sm !border !border-sky-500/30 !px-2 !py-1"
                />
              </div>

              {/* Color-scale legend so the temperature ramp has explicit
                  meaning — bottom of the map, above the hotspots strip. */}
              <div className="border-t border-sky-900/30 px-4 py-2 flex items-center gap-3 text-[10px] font-mono text-neutral-500 uppercase tracking-wider">
                <span className="shrink-0">Low</span>
                <div
                  className="flex-1 h-1.5 rounded-sm"
                  style={{
                    background:
                      "linear-gradient(to right, #10314f 0%, #15507f 28%, #1d6fb0 55%, #2b8fd4 80%, #5cb8f0 100%)",
                  }}
                />
                <span className="shrink-0">High</span>
              </div>

              {/* Inline top-5 hotspot leaderboard so the numbers stay scannable */}
              <div className="border-t border-sky-900/30 px-4 py-2.5 flex items-center gap-3 overflow-x-auto">
                <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider shrink-0">
                  Hotspots ·
                </span>
                {topCountries.slice(0, 5).map((c, i) => (
                  <span
                    key={c.name}
                    className="inline-flex items-center gap-1.5 text-[11px] shrink-0"
                  >
                    <span className="text-[10px] text-neutral-600 font-mono tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-sky-300">{c.name}</span>
                    <span className="font-mono tabular-nums text-neutral-300">
                      {fmtCompact(c.total)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
            {/* Infostealer surfaces — sits right of the map (2/5 of the row).
                Wider horizontal bars now that it has more room. */}
            <div className={`${cardClass} lg:col-span-2`}>
              <CardHeader
                icon={Layers}
                title="Infostealer surfaces · 30d"
                right={
                  <button
                    type="button"
                    onClick={() => navigate("/search")}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-300 hover:text-sky-200"
                  >
                    Pivot
                    <ArrowUpRight className="h-3 w-3" />
                  </button>
                }
              />
              <div className="p-4">
                {surfaceCategories.length === 0 ? (
                  <div className="text-sm text-neutral-500 py-6 text-center">No data.</div>
                ) : (
                  <ul className="space-y-3">
                    {(() => {
                      const max = Math.max(1, ...surfaceCategories.map((r) => r.value));
                      const total = surfaceCategories.reduce((s, r) => s + (r.value || 0), 0);
                      return surfaceCategories.map((r, i) => {
                        const w = max > 0 ? Math.max(2, (r.value / max) * 100) : 0;
                        const share = total > 0 ? Math.round((r.value / total) * 100) : 0;
                        return (
                          <li key={i}>
                            <div className="flex items-center justify-between gap-3 text-[12px] mb-1">
                              <span className="text-neutral-200 truncate" title={r.name}>
                                {r.name || "—"}
                              </span>
                              <span className="shrink-0 tabular-nums font-mono text-neutral-400">
                                {fmtCompact(r.value)} <span className="text-neutral-600">· {share}%</span>
                              </span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-white/[0.04] overflow-hidden">
                              <div className="h-full rounded-full bg-sky-600/80" style={{ width: `${w}%` }} />
                            </div>
                          </li>
                        );
                      });
                    })()}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}

          </>
          )}
        </main>
      </div>
    </div>
  );
}
